import { create } from 'zustand';
import { authApi } from '../../services/authApi';
import { getErrorMessage } from '../../services/apiClient';
import type { AuthStatus } from '../../types';

interface AuthState {
  status: AuthStatus;
  loading: boolean;
  error: string | null;
  
  // Actions
  checkStatus: () => Promise<void>;
  unlock: (passcode: string) => Promise<boolean>;
  lock: () => Promise<void>;
  clearError: () => void;
}

const initialStatus: AuthStatus = {
  authenticated: false,
  role: 'browser',
};

export const useAuthStore = create<AuthState>((set, get) => ({
  status: initialStatus,
  loading: true,
  error: null,

  checkStatus: async () => {
    try {
      set({ loading: true, error: null });
      const status = await authApi.getStatus();
      set({ status, loading: false });
    } catch (error) {
      set({ 
        status: initialStatus, 
        loading: false,
        error: null // Don't show error on initial check
      });
    }
  },

  unlock: async (passcode: string) => {
    try {
      set({ loading: true, error: null });
      const status = await authApi.unlock(passcode);
      set({ status, loading: false });
      return true;
    } catch (error: any) {
      // 使用改进的错误处理函数，自动识别网络错误和业务错误
      const message = getErrorMessage(error);
      set({ loading: false, error: message });
      return false;
    }
  },

  lock: async () => {
    try {
      set({ loading: true, error: null });
      await authApi.lock();
      set({ 
        status: { authenticated: false, role: 'browser' }, 
        loading: false 
      });
    } catch (error: any) {
      const message = error.response?.data?.detail || '锁定失败';
      set({ loading: false, error: message });
    }
  },

  clearError: () => set({ error: null }),
}));

// Selector hooks
export const useIsAdmin = () => useAuthStore((state) => state.status.role === 'admin');
export const useAuthLoading = () => useAuthStore((state) => state.loading);
export const useAuthError = () => useAuthStore((state) => state.error);
